import { FC, useEffect, useRef } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Svg, Typography } from '@altermaind/web/ui';
import { Employer } from '@/people/state/types/employer';
import { AccountStatus } from '@/swagger/constants/anagraphics-enums';
import { useDialog } from '../../hooks/useDialog';

type Props = {
  employer: Employer;
  onEdit: (edit: boolean) => void;
  setShowCtaProfile: (show: 'none' | 'block') => void;
  showCtaProfile: 'none' | 'block';
};

export const Settings: FC<Props> = ({ employer, onEdit, setShowCtaProfile, showCtaProfile }) => {
  const t = useTranslations('profileCard.editProfile.cta');

  const onClickSettings = () => {
    setShowCtaProfile(showCtaProfile === 'none' ? 'block' : 'none');
  };

  const settingsRef = useRef<HTMLDivElement>(null);

  const { openDialog: openDialogDisableProfile } = useDialog('disableProfile');
  const { openDialog: openDialogDeleteProfile } = useDialog('deleteProfile');
  const { openDialog: openDialogEnableProfile } = useDialog('enableProfile');

  useEffect(() => {
    if (showCtaProfile === 'none') return;

    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowCtaProfile('none');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowCtaProfile, showCtaProfile]);

  return (
    <>
      <Box
        style={{ cursor: 'pointer' }}
        onClick={onClickSettings}
        data-testid='employee-settings-button'
      >
        <Svg name='settings' size='s' />
      </Box>
      <Box
        ref={settingsRef}
        display={showCtaProfile}
        zIndex='1000'
        right='m'
        top='l'
        position='absolute'
      >
        <Card pr='l' pl='xs' py='xs' backgroundColor='grey/dark/background'>
          <Typography
            style={{ cursor: 'pointer' }}
            mb='xxxs'
            variant='p2/light'
            onClick={() => onEdit(true)}
          >
            {t('edit')}
          </Typography>
          {employer.status === AccountStatus.ENABLED && (
            <Typography
              onClick={openDialogDisableProfile}
              style={{ cursor: 'pointer' }}
              mb='xxxs'
              variant='p2/light'
            >
              {t('disabled')}
            </Typography>
          )}
          {employer.status === AccountStatus.DISABLED && (
            <Typography
              onClick={openDialogEnableProfile}
              style={{ cursor: 'pointer' }}
              mb='xxxs'
              variant='p2/light'
            >
              {t('enable')}
            </Typography>
          )}
          <Typography
            onClick={openDialogDeleteProfile}
            style={{ cursor: 'pointer' }}
            variant='p2/light'
            data-testid='employee-settings-delete'
          >
            {t('remove')}
          </Typography>
        </Card>
      </Box>
    </>
  );
};
